import { ApolloCache } from '@apollo/client';

import { Team } from '@graphql/generated';

/**
 * Cache cleanup for a team the current user no longer has access to — left
 * (including owner-transfer-then-leave) or deleted.
 *
 * Two steps, both required:
 * 1. Drop the team from every cached `listTeams` variant. `cache.modify`
 *    covers all of them (role-filtered, includePending true/false, …) without
 *    enumerating them.
 * 2. Evict the `Team` entity itself. TA-10471 made DrawerHeader's getTeam
 *    cache-first, so a warm, complete entity is served without a request —
 *    which means no error fires and the header's `teamError` cleanup never
 *    runs. Evicting forces any later read of that team to hit the network and
 *    surface the real "could not be found" error instead of stale data.
 */
export const removeTeamFromCache = (
  cache: ApolloCache<unknown>,
  teamId?: Team['id']
) => {
  if (!teamId) return;

  cache.modify({
    fields: {
      listTeams(existing = [], { readField }) {
        return Array.isArray(existing)
          ? existing.filter((teamRef) => readField('id', teamRef) !== teamId)
          : existing;
      },
    },
  });

  const cacheId = cache.identify({ __typename: 'Team', id: teamId });
  if (cacheId) {
    cache.evict({ id: cacheId });
    cache.gc();
  }
};
